import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type {
  LiveUpdatesChannel,
  LiveUpdatesContentFieldKey,
  LiveUpdatesStack,
  LiveUpdatesStatus,
  LiveUpdatesStatusKey,
  LiveUpdatesTriggerMode,
} from 'api/liveUpdates.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { MessageTextField } from './fields'
import {
  isOloProvider,
  OloWebhookPanel,
  SignalSelector,
  TriggerBadge,
} from './trigger'

const t = buildTranslate('live_updates.card')

// Exported so the scene can gate other Flybuy-specific UI on the same
// provider key, rather than re-declaring the literal.
export const FLYBUY_PROVIDER_KEY = 'flybuy'

// Order placed is fired by the customer's own checkout action in our UX, not
// by an incoming provider webhook — unlike every other event-mode status, so
// it never gets a "Triggered by <provider>" badge.
const PLACED_STATUS_KEY: LiveUpdatesStatusKey = 'placed'

// Card chrome (Figma): 1px grey border, 4px radius, 24px padding. The trigger
// pill overlaps the top border, so the card is positioned and the pill is
// pulled up over the edge.
const CARD_RADIUS = '4px'
const CARD_PADDING = '24px'
const CARD_GAP = '16px'
const BADGE_TOP_OFFSET = '-11px'
const BADGE_LEFT = '16px'
const COLLAPSED_PADDING = '16px 24px'

const channelLabel = (channel: LiveUpdatesChannel): string =>
  channel === 'push' ? t('channels.push') : t('channels.live_activity')

// FR: which stack slot backs this status's provider meta line (and the signal
// event-option provider) — Flybuy groups surface the status provider; delivery
// statuses prefer the delivery provider, everything else the ordering provider
// — each with a sensible fallback. `canceled_by_delivery` lives in the
// `canceled` section (FR-47) but is still delivery-triggered, so it's matched
// by status key alongside the `section === 'delivery'` rows (mirrors the
// same special-case in sections.ts). POS is not in the stack (AI-705) — there
// is no POS fallback here, matching StatusPresenter#event_trigger_label.
const signalProvider = (status: LiveUpdatesStatus, stack: LiveUpdatesStack) => {
  if (stack.statusProvider) return stack.statusProvider
  if (
    status.section === 'delivery' ||
    status.status === 'canceled_by_delivery'
  ) {
    return stack.deliveryProvider ?? stack.orderingProvider
  }
  return stack.orderingProvider
}

const providerName = (
  status: LiveUpdatesStatus,
  stack: LiveUpdatesStack
): string | undefined => signalProvider(status, stack)?.displayName

// The event-mode trigger badge copy. Derived here rather than read from the
// API's `trigger_source.label`, because nucleus computes that label from the
// PERSISTED trigger_mode (StatusPresenter#trigger_source): an unsaved switch
// from the timer to the event signal repainted the pill green while its text
// still read "Based on est. ready time" (AI-1016).
//
// Mirrors StatusPresenter#event_trigger_label — `signalProvider` already picks
// the same provider per status, and the stack's own display names carry the
// qualified forms that presenter special-cases ("Olo Dispatch", "Flybuy"), so
// naming the provider covers every branch. A stack with no nameable provider
// (an ordering key outside the display-name map) gets the same generic label
// the backend falls back to.
const eventTriggerLabel = (
  status: LiveUpdatesStatus,
  stack: LiveUpdatesStack
): string => {
  const provider = providerName(status, stack)
  return provider ? t('triggered_by', { provider }) : t('triggered_by_generic')
}

// Whether THIS status's event signal is sourced from Olo's webhook — gates the
// "Use the Olo event" card's connection-status block (SignalSelector), which
// only makes sense for the provider that actually owns the webhook.
const isOloSignalProvider = (
  status: LiveUpdatesStatus,
  stack: LiveUpdatesStack
): boolean => isOloProvider(signalProvider(status, stack))

export interface StatusCardProps {
  /** The (edit-overlaid) status this card renders — its content field values
   * already reflect any in-progress edits from the scene's buffer. */
  status: LiveUpdatesStatus
  /** The configuration's integration stack — drives the provider meta line. */
  stack: LiveUpdatesStack
  /** Read-only switch: when false every field is disabled and toggles are
   * inert. The scene always renders cards with `configurable: true` — a
   * non-configurable group shows the FR-76 billboard instead of any cards — so
   * this is a defensive presentational capability, exercised directly by the
   * component tests. */
  configurable: boolean
  /** Force every control disabled regardless of `configurable` — used to
   * freeze the card while a publish is in flight (fix 8). */
  disabled?: boolean
  /** Merchant has push & live activity updates disabled entirely — freezes
   * the "Push message" and "Live update text" fields, since guests won't
   * receive push or live activity notifications regardless of their content.
   * Status title stays editable (AI-939). */
  pushDisabled?: boolean
  /** Per-status server validation messages from a 400 publish envelope. */
  errors?: string[]
  /** Merchant-wide Olo webhook connection panel (undefined until the scene's
   * useOloWebhookConnection resolves) — rendered inside this status's "Use the
   * Olo event" card only when its signal is actually sourced from Olo. */
  oloWebhook?: OloWebhookPanel
  /** Callbacks are keyed by status so the scene can wire the config hook's
   * setters directly (no per-card closures). */
  onSetEnabled: (status: LiveUpdatesStatusKey, enabled: boolean) => void
  onSetTriggerMode: (
    status: LiveUpdatesStatusKey,
    mode: LiveUpdatesTriggerMode
  ) => void
  onSetTimerOffset: (status: LiveUpdatesStatusKey, minutes: number) => void
  onSetContent: (
    status: LiveUpdatesStatusKey,
    field: LiveUpdatesContentFieldKey,
    value: string
  ) => void
  /** Focus tracking so the preview panel can follow the edited field. */
  onFocusField?: (
    status: LiveUpdatesStatusKey,
    field: LiveUpdatesContentFieldKey
  ) => void
}

// One order-status card on the Live Updates config page: a trigger pill
// overlapping the top border, the status name + enable/disable control (or
// "Always enabled" for required statuses), the provider/message-type meta
// line, the optional kitchen-signal selector (timer-eligible statuses), and
// one message field per channel (FR-63). A disabled optional status collapses
// to a grey row with just its name + an Enable link (FR-34).
const StatusCard = ({
  status,
  stack,
  configurable,
  disabled = false,
  pushDisabled = false,
  errors,
  oloWebhook,
  onSetEnabled,
  onSetTriggerMode,
  onSetTimerOffset,
  onSetContent,
  onFocusField,
}: StatusCardProps) => {
  const [css] = useStyletron()

  const statusName = buildTranslate('live_updates.statuses')(status.status)
  const provider = providerName(status, stack)
  const showOloWebhookStatus = isOloSignalProvider(status, stack)
  // An Olo-signalled status may never be switched to the event signal until the
  // webhook is confirmed delivering events. `oloWebhook` is undefined in two
  // unrelated cases — this status isn't Olo-backed at all, and it IS but the
  // connection queries haven't settled — so the lock keys off the provider
  // check plus an explicitly 'connected' state. Reading it off the panel alone
  // would leave the option live during load, which is the one window a merchant
  // with no webhook could still select it.
  const oloEventUnavailable =
    showOloWebhookStatus && oloWebhook?.connectionState !== 'connected'

  const eventUnsupportedNotice = status.eventSupported
    ? undefined
    : t('signal.no_provider_event', {
        provider,
        event: status.stage.toLowerCase(),
        stage: status.stage,
      })
  const displayedTriggerMode: LiveUpdatesTriggerMode = status.eventSupported
    ? status.triggerMode
    : 'timer'

  const isRequired = status.required
  const isOptional = !isRequired
  // Inapplicable statuses (delivery rows on a non-delivery-capable stack,
  // FR-75) are read-only too — the publish endpoint 422s any edit to them.
  const readOnly = !configurable || disabled || !status.applicable
  const isCollapsed = isOptional && !status.enabled

  // A collapsed (disabled) optional status: grey row, name + Enable.
  if (isCollapsed) {
    return (
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: COLLAPSED_PADDING,
          borderRadius: CARD_RADIUS,
          backgroundColor: palette.grey10,
        })}
      >
        <Text.BodyText3 bold color="grey70">
          {statusName}
        </Text.BodyText3>
        <Button
          kind="minimal"
          disabled={readOnly}
          aria-label={t('enable_aria', { status: statusName })}
          onClick={() => onSetEnabled(status.status, true)}
        >
          {t('enable')}
        </Button>
      </div>
    )
  }

  const messageTypeText = status.channels.map(channelLabel).join(', ')

  const isFlybuyStack = stack.statusProvider?.key === FLYBUY_PROVIDER_KEY
  const isPushLockedByFlybuy =
    status.content.pushMessageText.editable === false &&
    status.applicable &&
    configurable &&
    isFlybuyStack

  // Shown for every timer-eligible, applicable status. When the card is
  // read-only (a publish is in flight, or — defensively — `configurable` is
  // false) it renders disabled rather than hidden, so the configured signal +
  // timer offset stay visible instead of the card hiding real configuration.
  const showSignalSelector = status.timerEligible && status.applicable

  // The `triggerMode === 'event'` half is belt-and-suspenders, not
  // load-bearing: `placed` is never timer-eligible (see fixtures/backend
  // defaults), so this only ever suppresses the event-mode "Triggered by
  // <provider>" badge in practice. Keeping the check means a hypothetical
  // future timer-eligible `placed` would still get its "Based on est. ready
  // time" badge instead of silently losing it too.
  const showTriggerBadge = !(
    status.status === PLACED_STATUS_KEY && status.triggerMode === 'event'
  )

  const showPushField = status.channels.includes('push')
  const showLiveUpdateField = status.channels.includes('live_activity')

  const renderField = (
    field: LiveUpdatesContentFieldKey,
    label: string,
    options: {
      multiline?: boolean
      locked?: boolean
      forceDisabled?: boolean
    } = {}
  ) => {
    const content = status.content[field]
    return (
      <MessageTextField
        label={label}
        fieldKey={field}
        value={content.value ?? ''}
        // The API-supplied default copy (nucleus DEFAULT_COPY), shown so the
        // merchant sees the live default without it counting as an edit. Empty
        // when the field has no default (or the backend hasn't sent it yet).
        placeholder={content.default ?? ''}
        maxLength={content.maxLength}
        editable={content.editable}
        multiline={options.multiline}
        lockedLabel={options.locked ? t('managed_by_flybuy') : undefined}
        lockedCaption={options.locked ? t('flybuy_push_note') : undefined}
        disabled={readOnly || options.forceDisabled}
        onChange={value => onSetContent(status.status, field, value)}
        onFocus={() => onFocusField?.(status.status, field)}
      />
    )
  }

  return (
    <div className={css({ position: 'relative' })}>
      {showTriggerBadge && (
        <div
          className={css({
            position: 'absolute',
            top: BADGE_TOP_OFFSET,
            left: BADGE_LEFT,
          })}
        >
          <TriggerBadge
            label={
              displayedTriggerMode === 'timer'
                ? t('based_on_est_ready_time')
                : eventTriggerLabel(status, stack)
            }
            detail={
              displayedTriggerMode === 'timer'
                ? undefined
                : status.triggerSource.detail
            }
            mode={displayedTriggerMode}
          />
        </div>
      )}

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: CARD_GAP,
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: palette.grey20,
          borderRadius: CARD_RADIUS,
          padding: CARD_PADDING,
          backgroundColor: palette.white,
        })}
      >
        <div>
          <div
            className={css({
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
            })}
          >
            <Text.Header4 tag="h3" className="m-0">
              {statusName}
            </Text.Header4>
            {isRequired ? (
              <Text.BodyText4 color="grey70">
                {t('always_enabled')}
              </Text.BodyText4>
            ) : (
              <Button
                kind="minimal"
                disabled={readOnly}
                aria-label={t('disable_aria', { status: statusName })}
                onClick={() => onSetEnabled(status.status, false)}
              >
                {t('disable')}
              </Button>
            )}
          </div>
          {/* Meta line (Figma 932:2646): each detail is a grey70 label + a
              grey90 value, the two details 24px apart. */}
          <div
            className={css({
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '24px',
              marginTop: '4px',
            })}
          >
            {provider && (
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                })}
              >
                <Text.BodyText4 color="grey70">{t('provider')}</Text.BodyText4>
                <Text.BodyText4 color="grey90">{provider}</Text.BodyText4>
              </div>
            )}
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              })}
            >
              <Text.BodyText4 color="grey70">
                {t('message_type')}
              </Text.BodyText4>
              <Text.BodyText4 color="grey90">{messageTypeText}</Text.BodyText4>
            </div>
          </div>
        </div>

        {showSignalSelector && (
          <SignalSelector
            triggerMode={status.triggerMode}
            timerOffset={status.timerOffset}
            timerOffsetValues={status.timerOffsetValues}
            timerDirection={status.timerDirection}
            eventOptionLabel={
              provider
                ? t('signal.use_provider_event', { provider })
                : t('signal.use_provider_event_generic')
            }
            disabled={readOnly}
            oloWebhook={showOloWebhookStatus ? oloWebhook : undefined}
            eventOptionUnavailable={oloEventUnavailable}
            eventUnsupportedNotice={eventUnsupportedNotice}
            onChangeMode={mode => onSetTriggerMode(status.status, mode)}
            onChangeOffset={minutes => onSetTimerOffset(status.status, minutes)}
          />
        )}

        {renderField('statusTitle', t('fields.status_title'))}

        {showPushField &&
          renderField('pushMessageText', t('fields.push_message'), {
            multiline: true,
            locked: isPushLockedByFlybuy,
            forceDisabled: pushDisabled,
          })}

        {showLiveUpdateField &&
          renderField('homescreenText', t('fields.live_update_text'), {
            forceDisabled: pushDisabled,
          })}

        {errors && errors.length > 0 && (
          <ul
            aria-live="polite"
            className={css({ margin: 0, paddingLeft: '20px' })}
          >
            {errors.map(error => (
              <li key={error}>
                <Text.BodyText5 color="cayenne50">{error}</Text.BodyText5>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default StatusCard
